import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const BlogSEO = ({ type = 'overview', article = null, category = null }) => {
  const { language } = useLanguage();
  const baseUrl = 'https://dionhairclinic.de';

  // Default blog overview config
  let config = {
    title: language === 'de' 
      ? 'Blog & Ratgeber | Haartransplantation Wissen | Dion Hair Clinic'
      : 'Blog & Guide | Hair Transplant Knowledge | Dion Hair Clinic',
    description: language === 'de'
      ? 'Expertenwissen zu Haartransplantation, FUE, DHI, Barttransplantation und mehr. Fundierte Artikel von den Spezialisten der Dion Hair Clinic.'
      : 'Expert knowledge on hair transplantation, FUE, DHI, beard transplantation and more. Well-founded articles from the specialists at Dion Hair Clinic.',
    canonical: `${baseUrl}/blog`,
    image: `${baseUrl}/og-image.jpg`
  };

  // Article-specific config
  if (type === 'article' && article) {
    config = {
      title: language === 'de' ? article.title : article.titleEn,
      description: language === 'de' ? article.metaDescription : article.metaDescriptionEn,
      canonical: `${baseUrl}/blog/${article.slug}`,
      image: article.featuredImage || `${baseUrl}/og-image.jpg`,
      publishedTime: article.publishedAt,
      author: article.author
    };
  }

  // Category-specific config
  if (type === 'category' && category) {
    const categoryName = language === 'de' ? category.name : category.nameEn;
    config = {
      title: `${categoryName} | Blog | Dion Hair Clinic`,
      description: language === 'de'
        ? `Alle Artikel zum Thema ${categoryName}. Expertenwissen von der Dion Hair Clinic.`
        : `All articles about ${categoryName}. Expert knowledge from Dion Hair Clinic.`,
      canonical: `${baseUrl}/blog/kategorie/${category.slug}`,
      image: `${baseUrl}/og-image.jpg`
    };
  }

  // Update document meta tags directly for SPA
  useEffect(() => {
    document.title = config.title;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', config.description);
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', config.canonical);
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', config.title);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', config.description);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', config.canonical);
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute('content', config.image);
    
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', config.title);
    
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', config.description);
    
    const twImage = document.querySelector('meta[property="twitter:image"]');
    if (twImage) twImage.setAttribute('content', config.image);
    
  }, [config]);

  // Generate FAQ structured data for articles
  const generateFAQStructuredData = () => {
    if (type !== 'article' || !article?.faq?.length) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": article.faq.map(item => ({
        "@type": "Question",
        "name": language === 'de' ? item.question : item.questionEn,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": language === 'de' ? item.answer : item.answerEn
        }
      }))
    };
  };

  // Generate Article structured data
  const generateArticleStructuredData = () => {
    if (type !== 'article' || !article) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": language === 'de' ? article.title : article.titleEn,
      "description": language === 'de' ? article.metaDescription : article.metaDescriptionEn,
      "image": article.featuredImage,
      "author": {
        "@type": "Organization",
        "name": "Dion Hair Clinic"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Dion Hair Clinic",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/favicon-512.png`
        }
      },
      "datePublished": article.publishedAt,
      "dateModified": article.publishedAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": config.canonical
      }
    };
  };

  const faqStructuredData = generateFAQStructuredData();
  const articleStructuredData = generateArticleStructuredData();

  return (
    <Helmet>
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <link rel="canonical" href={config.canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:url" content={config.canonical} />
      <meta property="og:image" content={config.image} />
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      
      {/* Twitter */}
      <meta property="twitter:title" content={config.title} />
      <meta property="twitter:description" content={config.description} />
      <meta property="twitter:image" content={config.image} />
      
      {/* Article-specific meta */}
      {type === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={language === 'de' ? article.categoryName : article.categoryNameEn} />
        </>
      )}
      
      {/* Structured Data */}
      {articleStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      )}
      {faqStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default BlogSEO;
